/**
 * The guard is NAVIGATION, not authorization.
 *
 * It decides which route the browser may PAINT; it never decides what data may
 * be read. This bundle is public by construction — every route name, every
 * copy string, and every fetch site in it can be read by anyone who downloads
 * it — so authorization stays where it always was: enforced by the owning
 * backend on every request, and the origin a call arrives from is never an
 * authorization input.
 *
 * A guard that hides a route is correct. A guard TRUSTED TO PROTECT DATA is a
 * defect, and the failure is silent and cross-repo.
 *
 * Boot order matters: a hard refresh arrives with no access token, which is
 * normal rather than signed-out. The app attempts exactly one refresh against
 * the httpOnly cookie and only then lets the guard decide, so a reload does
 * not eject a signed-in subscriber to the sign-in screen.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import {
  bootstrapSession,
  currentPath,
  hasSession,
  loginPathFor,
  validateNext,
  NEXT_PARAM,
} from '@shared/auth';
import { warmupForRoute } from '@shared/telemetry';

import { PendingRoute } from './PendingRoute';

type BootState = 'pending' | 'settled';

/**
 * The boot refresh, as a condition any route can wait on.
 *
 * Extracted because waiting for it and REDIRECTING on its answer are two
 * different jobs, and the system pages need the first without the second.
 */
function useBootSettled(): BootState {
  const [boot, setBoot] = useState<BootState>(() => (hasSession() ? 'settled' : 'pending'));

  useEffect(() => {
    if (boot === 'settled') return;
    let alive = true;
    void bootstrapSession().finally(() => {
      if (alive) setBoot('settled');
    });
    return () => {
      alive = false;
    };
  }, [boot]);

  return boot;
}

export function AuthGuard({ children }: { children: ReactNode }) {
  const boot = useBootSettled();

  useEffect(() => {
    // Never warm a route the guard is about to replace.
    if (boot === 'settled' && hasSession()) warmupForRoute();
  }, [boot]);

  if (boot === 'pending') return <PendingRoute />;
  if (!hasSession()) return <Navigate to={loginPathFor(currentPath())} replace />;

  return <>{children}</>;
}

/**
 * The system pages' gate: WAIT for the boot refresh, then render whatever the
 * answer was.
 *
 * `/404` and `/500` are deliberately unguarded — a reader on a dead address is
 * more lost than anywhere else, and the shell is the only route out this area
 * owns — but they render inside the FULL shell, whose top bar mounts the
 * notification handle, and that handle reads as soon as it mounts. With no
 * gate at all it fired `GET /notifications/unreadCount` from a session the
 * boot refresh had not yet established: a red 401 in the console on every
 * entry to these three levels, healed a moment later by the refresh landing.
 * `PendingRoute`'s own comment names exactly this failure.
 *
 * It is NOT `AuthGuard`: no redirect, no session requirement. Only the wait.
 */
export function BootGate({ children }: { children: ReactNode }) {
  const boot = useBootSettled();

  if (boot === 'pending') return <PendingRoute />;

  return <>{children}</>;
}

/**
 * The REVERSE of `AuthGuard`: a reader who already holds a session has no
 * business on the sign-in screen.
 *
 * Without it, `/login` painted its whole form for someone already signed in —
 * reachable from the browser's own history, from a bookmark, and from a landing
 * page that still linked to it. Submitting there mints a SECOND session over a
 * live one; abandoning it leaves the reader convinced they are signed out while
 * every other tab says otherwise. Neither is a state the product has an answer
 * for, so the screen is not drawn at all.
 *
 * It waits for the boot refresh first, for the same reason `AuthGuard` does: a
 * hard refresh arrives with no access token, and answering before the refresh
 * lands would treat every signed-in reader as anonymous.
 *
 * The destination is the `next` the address already carries — validated
 * same-origin by `validateNext`, which answers `/` for anything else. That is
 * what makes the guard invisible in the ordinary flow: a reader sent to
 * `/login?next=/titles/x` who turns out to still hold a session lands on the
 * title they asked for rather than on a home page that forgot it.
 */
export function GuestGuard({ children }: { children: ReactNode }) {
  const boot = useBootSettled();
  const [params] = useSearchParams();

  if (boot === 'pending') return <PendingRoute />;
  if (hasSession()) return <Navigate to={validateNext(params.get(NEXT_PARAM))} replace />;

  return <>{children}</>;
}
